import { useMemo, useState } from 'react'
import moment from 'moment-timezone'
import _ from 'lodash'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { Box, IconButton, Tooltip } from '@mui/material'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'

import MapPopup from '/components/passes/map-popup'

import DataExportDialog from "../data-export-dialog"
import excelExport from './scans-to-excel-stampcard'


const ScanTable = ({ scans, loading, templateId, passId }) => {

  const totalStampsAdded = useMemo(() => _.sum(scans.map(d => d.stamps ?? 0)), [scans])
  const totalFullCardsRedeemed = useMemo(() => _.sum(scans.map(d => d.cards ?? 0)), [scans])

  const [scan,setScan] = useState()
  const [mapOpen,setMapOpen] = useState(false)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const openMap = scan => {
    setScan(scan)
    setMapOpen(true)
  }

  const columns = useMemo(() => [
    {
      id: 'timestamp',
      accessorFn: (originalRow) => moment(originalRow.timestamp, "YYYYMMDDHHmmss"),
      header: 'Scanned',
      Cell: ({ cell }) => cell.getValue().format("DD MMM YYYY HH:mm"),
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 180,
      Footer: () => "Totals"
    },
    {
      id: "admin_adjustment",
      accessorFn: (originalRow) => originalRow.admin,
      header: "Admin Adjustment",
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      size: 200,
      Cell: ({ cell }) => cell.getValue() ? <AutoAwesomeOutlinedIcon /> : null
    },    
    {
      accessorKey: 'action',
      header: 'Action',
      size: 180,
    },
    {
      accessorKey: 'stamps',
      header: 'Stamps',
      size: 20,
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalStampsAdded
    },  
    {
      accessorKey: 'cards',
      header: 'Cards',
      size: 20,
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalFullCardsRedeemed
    },  
    {
      accessorKey: 'reference',
      header: 'Reference',
      filterVariant: 'autocomplete',
      size: 200,
    },
    {
      accessorKey: 'username',
      header: 'Scanned By',
      filterVariant: 'autocomplete',
      size: 200,
    },
    {
      id: 'position',
      accessorFn: (originalRow) => originalRow,
      header: 'Scan Location',
      filterVariant: 'autocomplete',
      size: 400,
      Cell: ({ cell }) => {
        const row = cell.getValue()
        return row.position ? <div className='link' onClick={() => openMap(row)}><RoomOutlinedIcon className='mr-5' />{row.position.x},{row.position.y}</div> : null
      }
    },
  ])

  const table = useMaterialReactTable({
    columns,
    data: scans,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id,
    muiTablePaperProps: {
      sx: {
        top: 180,
        bottom: 5,
        left: 5,
        right: 5,
        position: "fixed"
      },
      elevation: 3,
    },
    muiTableContainerProps: {
      sx: {
        top: 220,
        bottom: 5,
        left: 5,
        right: 5,
        position: "fixed"
      }
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
  })

  return (
    <>
      <MapPopup scan={scan} open={mapOpen} setOpen={setMapOpen}/>
      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${templateId}-(${passId})-scan-history`} data={scans} excelExport={excelExport} />
      <MaterialReactTable table={table} />
    </>
  )

}

export default ScanTable